import React from 'react';
import { useTranslation } from 'react-i18next';

import LanguageIcon from '@mui/icons-material/Language';
import LogoutIcon from '@mui/icons-material/Logout';
import { IconButton, Menu, MenuItem, Box, useMediaQuery, useTheme, Button } from '@mui/material';

import { useLogout } from './Menu/menu.actions';
import LanguageFlag from '../LanguageFlag';
import { useAllowedLanguages } from '../../utils/allowed-languages';
import { APP_LANGUAGE_OPTIONS, getLanguageOption } from '../../utils/flag-utils';

import styles from './header.module.scss';
import RetryImg from '@shared/components/RetryImg';

interface HeaderProps {
  gameKey?: string;
}

const Header: React.FC<HeaderProps> = ({ gameKey }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const allowedLanguages = useAllowedLanguages();
  const languageOptions = allowedLanguages
    ? allowedLanguages.flatMap((code) => APP_LANGUAGE_OPTIONS.filter((option) => option.code === code))
    : APP_LANGUAGE_OPTIONS;
  
  const [langMenuAnchor, setLangMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [, handleLogout, logoutLoading] = useLogout();

  const handleLangClick = (event: React.MouseEvent<HTMLElement>) => {
    setLangMenuAnchor(event.currentTarget);
  };
  const handleLangClose = () => {
    setLangMenuAnchor(null);
  };
  const handleLanguageChange = (lng: string) => {
    i18n.changeLanguage(lng);
    handleLangClose();
  };

  const handleLogoutButtonClick = (e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Предотвращаем множественные клики
    if (logoutLoading) {
      return;
    }
    
    handleLogout();
  };
  
  const handleLogoutKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleLogoutButtonClick(e);
    }
  };

  const handleFaviconClick = () => {
    window.open('https://gameinvitation.com', '_blank');
  };

  return (
    <header className={styles.header}>
      <div className={styles.left}>
          <IconButton 
            onClick={handleFaviconClick}
            className={styles.faviconBtn}
            sx={{ 
              padding: '4px',
              '&:hover': {
                opacity: 0.8
              }
            }}
          >
            <RetryImg 
              src="/favicon.ico" 
              alt="GameInvitation" 
              style={{ 
                width: '24px', 
                height: '24px',
                cursor: 'pointer'
              }} 
            />
          </IconButton>
                 {!isMobile && (
           <span 
             onClick={handleFaviconClick}
             className={styles.logoLabel}
           >
             GameInvitation.com
           </span>
        )}
      </div>
      <div className={styles.center}>
        {isMobile && gameKey ? (
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              fontSize: '0.7rem',
              fontWeight: 500,
              color: 'text.primary',
              textAlign: 'center',
              whiteSpace: 'nowrap'
            }}
          >
            {gameKey}
          </Box>
        ) : !isMobile ? (
          <>
            <span className={styles.langText}>{t('global.language')}</span>
            <IconButton 
              onClick={handleLangClick} 
              className={`${styles.langBtn} ${styles.textFlag}`}
              sx={{ minWidth: 56, px: 1.5 }}
            >
              <Box component="span" sx={{ fontSize: '0.8rem', fontWeight: 600, lineHeight: 1 }}>
                {getLanguageOption(i18n.language).shortLabel}
              </Box>
            </IconButton>
          </>
        ) : null /* на телефоне кнопка языка справа; без этого у гостя было две */}
        <Menu
          anchorEl={langMenuAnchor}
          open={Boolean(langMenuAnchor)}
          onClose={handleLangClose}
        >
          {languageOptions.map((languageOption) => (
            <MenuItem key={languageOption.code} onClick={() => handleLanguageChange(languageOption.code)}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5 }}>
                <LanguageFlag language={languageOption.code} size={14} />
                <span>{t(`global.languages.${languageOption.translationKey}`)}</span>
              </Box>
            </MenuItem>
          ))}
        </Menu>
      </div>
      <div className={styles.right}>
        {!isMobile && gameKey && <span className={styles.gameKey}>{gameKey}</span>}
        {isMobile && (
          <IconButton 
            onClick={handleLangClick} 
            className={`${styles.langBtn} ${styles.textFlag}`}
            sx={{ minWidth: 56, px: 1.5 }}
          >
            <Box component="span" sx={{ fontSize: '0.8rem', fontWeight: 600, lineHeight: 1 }}>
              {getLanguageOption(i18n.language).shortLabel}
            </Box>
          </IconButton>
        )}
        {/* Кнопка LOGOUT вместо ключа */}
        {!isMobile ? (
          <Button
            onClick={handleLogoutButtonClick}
            onKeyDown={handleLogoutKeyDown}
            disabled={logoutLoading}
            variant="outlined"
            size="small"
            startIcon={<LogoutIcon />}
            type="button"
            aria-label={t('global.LOGOUT')}
            sx={{
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              color: '#666',
              borderColor: '#ddd',
              pointerEvents: logoutLoading ? 'none' : 'auto',
              cursor: logoutLoading ? 'not-allowed' : 'pointer',
              position: 'relative',
              zIndex: 10,
              '&:hover:not(:disabled)': {
                borderColor: '#999',
                backgroundColor: '#f5f5f5'
              },
              '&:focus-visible': {
                outline: '2px solid #3b82f6',
                outlineOffset: '2px'
              },
              '&:disabled': {
                opacity: 0.6,
                cursor: 'not-allowed'
              }
            }}
          >
            {t('global.LOGOUT')}
          </Button>
        ) : (
          <IconButton
            onClick={handleLogoutButtonClick}
            onKeyDown={handleLogoutKeyDown}
            disabled={logoutLoading}
            size="small"
            type="button"
            aria-label={t('global.LOGOUT')}
            sx={{
              color: '#666',
              pointerEvents: logoutLoading ? 'none' : 'auto',
              cursor: logoutLoading ? 'not-allowed' : 'pointer',
              position: 'relative',
              zIndex: 10,
              '&:hover:not(:disabled)': {
                backgroundColor: '#f5f5f5'
              },
              '&:focus-visible': {
                outline: '2px solid #3b82f6',
                outlineOffset: '2px'
              },
              '&:disabled': {
                opacity: 0.6,
                cursor: 'not-allowed'
              }
            }}
          >
            <LogoutIcon />
          </IconButton>
        )}
      </div>
    </header>
  );
};

export default Header; 
